import React from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { ROUTES } from "../../../../shared/utils";

interface NavItem {
  key: string;
  label: string;
  icon: React.ReactElement;
  path: string;
  matchPattern?: string;
  centralAdminOnly?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: "CLIENTS",
    items: [
      {
        key: "clients",
        label: "Clients",
        icon: <BusinessIcon fontSize="small" />,
        path: ROUTES.SETTINGS_CLIENTS,
        matchPattern: `${ROUTES.SETTINGS_CLIENTS}/*`,
      },
    ],
  },
  {
    label: "PARTICIPANTS",
    items: [
      {
        key: "participants",
        label: "Participants",
        icon: <PeopleIcon fontSize="small" />,
        path: ROUTES.SETTINGS_PARTICIPANTS,
        matchPattern: `${ROUTES.SETTINGS_PARTICIPANTS}/*`,
        centralAdminOnly: true,
      },
      {
        key: "participant-settings",
        label: "Participant Settings",
        icon: <PersonIcon fontSize="small" />,
        path: ROUTES.SETTINGS_PARTICIPANT_SETTINGS,
        centralAdminOnly: true,
      },
      {
        key: "pre-registrations",
        label: "Pre-Registrations",
        icon: <HowToRegIcon fontSize="small" />,
        path: ROUTES.SETTINGS_PRE_REGISTRATIONS,
        centralAdminOnly: true,
      },
    ],
  },
  {
    label: "CONTENT",
    items: [
      {
        key: "wellbeing-content",
        label: "Wellbeing Content",
        icon: <FavoriteIcon fontSize="small" />,
        path: ROUTES.SETTINGS_WELLBEING_CONTENT,
        centralAdminOnly: true,
      },
    ],
  },
  {
    label: "PLATFORM",
    items: [
      {
        key: "platform",
        label: "Platform Settings",
        icon: <SettingsIcon fontSize="small" />,
        path: ROUTES.SETTINGS_PLATFORM,
        centralAdminOnly: true,
      },
    ],
  },
];

interface SettingsNavProps {
  isCentralAdmin: boolean;
}

export const SettingsNav: React.FC<SettingsNavProps> = ({ isCentralAdmin }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (item: NavItem) => {
    const pattern = item.matchPattern ?? item.path;
    return !!matchPath({ path: pattern, end: false }, location.pathname);
  };

  return (
    <Box sx={{ py: 1 }}>
      {NAV_GROUPS.map((group, groupIdx) => {
        const visibleItems = group.items.filter(
          (item) => !item.centralAdminOnly || isCentralAdmin
        );
        if (!visibleItems.length) return null;

        return (
          <Box key={group.label}>
            {groupIdx > 0 && <Divider sx={{ my: 1, mx: 2 }} />}
            <Typography
              variant="overline"
              color="text.disabled"
              sx={{
                px: 2,
                pt: 1,
                pb: 0.5,
                display: "block",
                fontSize: "0.62rem",
                letterSpacing: "0.09em",
              }}
            >
              {group.label}
            </Typography>
            <List disablePadding>
              {visibleItems.map((item) => {
                const active = isActive(item);
                return (
                  <ListItemButton
                    key={item.key}
                    onClick={() => navigate(item.path)}
                    sx={{
                      mx: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      mb: 0.25,
                      bgcolor: active
                        ? alpha(theme.palette.primary.main, 0.1)
                        : "transparent",
                      "&:hover": {
                        bgcolor: active
                          ? alpha(theme.palette.primary.main, 0.15)
                          : theme.palette.action.hover,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 30,
                        color: active ? "primary.main" : "text.secondary",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: 13,
                            fontWeight: active ? 600 : 400,
                            color: active ? "primary.main" : "text.primary",
                          },
                        },
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        );
      })}
    </Box>
  );
};
